/**
 * Geld. Regel 1: altijd een integer in centen, nooit een float.
 *
 * Alles wat met bedragen rekent staat hier of gebruikt deze functies.
 * Buiten packages/core wordt er niet afgerond.
 */

/** Een bedrag in hele centen. Positief of negatief (een retour is negatief). */
export type Cent = number;

/** Een percentage in basispunten: 1% = 100, 12,5% = 1250, 100% = 10000. */
export type Basispunten = number;

export const BASISPUNTEN_NOEMER = 10_000;

export class GeldFout extends Error {
  constructor(bericht: string) {
    super(bericht);
    this.name = 'GeldFout';
  }
}

/** Controleert dat een waarde een bruikbaar bedrag in hele centen is. */
export function isCent(waarde: unknown): waarde is Cent {
  return typeof waarde === 'number' && Number.isSafeInteger(waarde);
}

export function eisCent(waarde: unknown, veld = 'bedrag'): Cent {
  if (!isCent(waarde)) {
    throw new GeldFout(`${veld} moet een geheel aantal centen zijn, kreeg: ${String(waarde)}`);
  }
  return waarde;
}

/**
 * Rondt af op hele centen, half naar boven en van nul af.
 * 0,5 cent wordt 1 cent; -0,5 cent wordt -1 cent.
 * Dit is de enige plek in het project waar geldafronding gebeurt.
 */
export function afrondCenten(waarde: number): Cent {
  if (!Number.isFinite(waarde)) {
    throw new GeldFout(`kan ${String(waarde)} niet afronden`);
  }
  const afgerond = waarde < 0 ? -Math.round(-waarde) : Math.round(waarde);
  if (!Number.isSafeInteger(afgerond)) {
    throw new GeldFout(`bedrag valt buiten het veilige bereik: ${waarde}`);
  }
  return afgerond;
}

/** Telt bedragen op. Lege lijst is 0. */
export function sommeer(bedragen: readonly Cent[]): Cent {
  let totaal = 0;
  for (const bedrag of bedragen) {
    totaal += eisCent(bedrag);
  }
  if (!Number.isSafeInteger(totaal)) {
    throw new GeldFout('optelling valt buiten het veilige bereik');
  }
  return totaal;
}

/** Stukprijs maal aantal. Aantal is een geheel getal >= 0. */
export function vermenigvuldig(stukprijs: Cent, aantal: number): Cent {
  eisCent(stukprijs, 'stukprijs');
  if (!Number.isSafeInteger(aantal) || aantal < 0) {
    throw new GeldFout(`aantal moet een geheel getal >= 0 zijn, kreeg: ${String(aantal)}`);
  }
  const totaal = stukprijs * aantal;
  if (!Number.isSafeInteger(totaal)) {
    throw new GeldFout('regeltotaal valt buiten het veilige bereik');
  }
  return totaal;
}

/**
 * Neemt een percentage van een bedrag, met het percentage in basispunten.
 * Rekent in gehele centen; het resultaat is afgerond.
 */
export function percentageVan(bedrag: Cent, basispunten: Basispunten): Cent {
  eisCent(bedrag);
  if (!Number.isSafeInteger(basispunten) || basispunten < 0) {
    throw new GeldFout(`basispunten moeten een geheel getal >= 0 zijn, kreeg: ${basispunten}`);
  }
  const product = bedrag * basispunten;
  if (!Number.isSafeInteger(product)) {
    throw new GeldFout('percentageberekening valt buiten het veilige bereik');
  }
  return afrondCenten(product / BASISPUNTEN_NOEMER);
}

/**
 * Verdeelt een bedrag over gewichten zonder centen kwijt te raken.
 * Gebruikt de grootste-rest-methode: iedereen krijgt zijn hele deel, de
 * overgebleven centen gaan naar de grootste resten. De som van het resultaat
 * is exact gelijk aan het ingelegde bedrag.
 *
 * Gebruikt voor het doorbelasten van transactiekosten over kramen.
 */
export function verdeelProportioneel(bedrag: Cent, gewichten: readonly number[]): Cent[] {
  eisCent(bedrag);
  if (gewichten.length === 0) {
    throw new GeldFout('verdelen kan niet zonder gewichten');
  }
  for (const gewicht of gewichten) {
    if (!Number.isFinite(gewicht) || gewicht < 0) {
      throw new GeldFout(`gewicht moet >= 0 zijn, kreeg: ${String(gewicht)}`);
    }
  }

  const somGewichten = gewichten.reduce((a, b) => a + b, 0);
  if (somGewichten === 0) {
    // Niets om aan te verdelen: alles naar de eerste, zodat de som klopt.
    return gewichten.map((_, i) => (i === 0 ? bedrag : 0));
  }

  const teken = bedrag < 0 ? -1 : 1;
  const absoluut = Math.abs(bedrag);

  const exact = gewichten.map((gewicht) => (absoluut * gewicht) / somGewichten);
  const heel = exact.map((waarde) => Math.floor(waarde));
  const uitgedeeld = heel.reduce((a, b) => a + b, 0);
  let restant = absoluut - uitgedeeld;

  const volgorde = exact
    .map((waarde, index) => ({ index, rest: waarde - Math.floor(waarde) }))
    .sort((a, b) => b.rest - a.rest || a.index - b.index);

  const resultaat = [...heel];
  for (const { index } of volgorde) {
    if (restant <= 0) break;
    resultaat[index] = (resultaat[index] ?? 0) + 1;
    restant -= 1;
  }

  return resultaat.map((waarde) => waarde * teken);
}

/**
 * Toont een bedrag als "€ 12,50". Bewust niet via Intl, zodat de uitvoer
 * overal exact hetzelfde is: op een toestel, in een PDF en in een test.
 */
export function formatteerEuro(bedrag: Cent, opties: { metSymbool?: boolean } = {}): string {
  eisCent(bedrag);
  const metSymbool = opties.metSymbool ?? true;
  const negatief = bedrag < 0;
  const absoluut = Math.abs(bedrag);
  const euros = Math.floor(absoluut / 100);
  const centen = absoluut % 100;

  const eurosMetPunten = String(euros).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const kern = `${eurosMetPunten},${String(centen).padStart(2, '0')}`;

  return `${negatief ? '-' : ''}${metSymbool ? '€ ' : ''}${kern}`;
}

/**
 * Leest een ingetypt bedrag als centen. Accepteert "12,50", "12.50", "€ 12,50",
 * "1.234,56" en "1234". Weigert alles wat dubbelzinnig is.
 */
export function parseEuroNaarCenten(invoer: string): Cent {
  const opgeschoond = invoer.trim().replace(/[€\s\u00a0]/g, '');
  if (opgeschoond === '') {
    throw new GeldFout('leeg bedrag');
  }

  const negatief = opgeschoond.startsWith('-');
  const zonderTeken = negatief ? opgeschoond.slice(1) : opgeschoond;

  if (!/^[\d.,]+$/.test(zonderTeken)) {
    throw new GeldFout(`onleesbaar bedrag: ${invoer}`);
  }

  const laatsteKomma = zonderTeken.lastIndexOf(',');
  const laatstePunt = zonderTeken.lastIndexOf('.');
  const scheidingsteken = laatsteKomma > laatstePunt ? ',' : laatstePunt > -1 ? '.' : '';

  let heel = zonderTeken;
  let decimalen = '';
  if (scheidingsteken !== '') {
    const positie = zonderTeken.lastIndexOf(scheidingsteken);
    const staart = zonderTeken.slice(positie + 1);
    // Een punt met precies drie cijfers erachter is een duizendtalscheiding
    // ("1.234"), geen decimalen. Een komma is in het Nederlands altijd het
    // decimaalteken, dus "12,505" is geen 12505 maar een fout.
    const isDuizendtal = scheidingsteken === '.' && staart.length === 3 && zonderTeken.length > 4;
    if (isDuizendtal) {
      heel = zonderTeken;
      decimalen = '';
    } else {
      heel = zonderTeken.slice(0, positie);
      decimalen = staart;
    }
  }

  heel = heel.replace(/[.,]/g, '');
  if (decimalen.length > 2) {
    throw new GeldFout(`meer dan twee decimalen: ${invoer}`);
  }
  if (!/^\d*$/.test(heel) || !/^\d*$/.test(decimalen)) {
    throw new GeldFout(`onleesbaar bedrag: ${invoer}`);
  }

  const centen = Number(heel || '0') * 100 + Number(decimalen.padEnd(2, '0') || '0');
  if (!Number.isSafeInteger(centen)) {
    throw new GeldFout(`bedrag te groot: ${invoer}`);
  }
  return negatief ? -centen : centen;
}

/**
 * Leest een percentage dat iemand intypt als basispunten. "12,5" wordt 1250.
 *
 * Dit staat hier en niet in een scherm, omdat een percentage via de commissie
 * rechtstreeks in het geld terechtkomt. Meer dan twee decimalen kan niet: die
 * passen niet in een geheel aantal basispunten, en stilletjes afronden op een
 * percentage dat over de omzet gaat is precies wat regel 1 verbiedt.
 */
export function procentNaarBasispunten(invoer: string | number): Basispunten {
  const tekst = typeof invoer === 'number' ? String(invoer) : invoer.trim().replace(',', '.');
  if (tekst === '') {
    throw new GeldFout('Vul een percentage in.');
  }
  if (!/^\d+(\.\d{1,2})?$/.test(tekst)) {
    throw new GeldFout(
      'Vul een percentage in met hoogstens twee decimalen, bijvoorbeeld 10 of 12,5.',
    );
  }

  const getal = Number(tekst);
  if (getal < 0 || getal > 100) {
    throw new GeldFout('Een percentage ligt tussen 0 en 100.');
  }

  // Via hele honderdsten, zodat 12,5 * 100 nooit 1249,9999 kan worden.
  const [heel = '0', decimalen = ''] = tekst.split('.');
  return Number(heel) * 100 + Number(decimalen.padEnd(2, '0') || '0');
}

/** Toont basispunten als percentage: 1250 wordt "12,5". */
export function basispuntenNaarProcent(basispunten: Basispunten): string {
  if (!Number.isSafeInteger(basispunten) || basispunten < 0) {
    throw new GeldFout(`basispunten moeten een geheel getal >= 0 zijn, kreeg ${basispunten}`);
  }
  const heel = Math.floor(basispunten / 100);
  const rest = basispunten % 100;
  if (rest === 0) return String(heel);
  return `${heel},${String(rest).padStart(2, '0').replace(/0$/, '')}`;
}

/**
 * Een tarief in tienden van een cent.
 *
 * De transactietarieven van een bank hebben meer cijfers achter de komma dan
 * een gewoon bedrag: Rabobank rekent € 0,066 per pintransactie. Dat afronden op
 * hele centen is geen detail — over 640.000 transacties per jaar scheelt naar
 * boven of naar beneden afronden ruim € 2.500. Daarom bewaren we zulke
 * tarieven als geheel getal in tienden van een cent, en ronden we pas af als er
 * een echt bedrag uit moet komen.
 *
 * € 0,066 = 6,6 cent = 66 tienden van een cent.
 */
export type TiendeCent = number;

export function eisTiendeCent(waarde: unknown, veld = 'tarief'): TiendeCent {
  if (typeof waarde !== 'number' || !Number.isSafeInteger(waarde)) {
    throw new GeldFout(
      `${veld} moet een geheel aantal tienden van een cent zijn, kreeg: ${String(waarde)}`,
    );
  }
  return waarde;
}

/** Rekent een aantal maal een tarief om naar hele centen, één keer afgerond. */
export function tariefMaalAantal(tarief: TiendeCent, aantal: number): Cent {
  eisTiendeCent(tarief);
  if (!Number.isSafeInteger(aantal) || aantal < 0) {
    throw new GeldFout(`aantal moet een geheel getal >= 0 zijn, kreeg: ${String(aantal)}`);
  }
  const tienden = tarief * aantal;
  if (!Number.isSafeInteger(tienden)) {
    throw new GeldFout('tariefberekening valt buiten het veilige bereik');
  }
  return afrondCenten(tienden / 10);
}

/**
 * Leest een tarief zoals het op een tarievenblad staat: "0,066" wordt 66.
 * Hoogstens drie decimalen, want verder gaat geen enkele bank.
 */
export function parseTariefNaarTiendeCenten(invoer: string): TiendeCent {
  const opgeschoond = invoer
    .trim()
    .replace(/[€\s\u00a0]/g, '')
    .replace(',', '.');
  if (opgeschoond === '') {
    throw new GeldFout('Vul een tarief in.');
  }
  if (!/^\d+(\.\d{1,3})?$/.test(opgeschoond)) {
    throw new GeldFout('Vul een tarief in met hoogstens drie decimalen, bijvoorbeeld 0,066.');
  }
  const [heel = '0', decimalen = ''] = opgeschoond.split('.');
  // Euro's naar tienden van een cent: × 1000.
  return Number(heel) * 1_000 + Number(decimalen.padEnd(3, '0') || '0');
}

/** Toont een tarief zoals op een tarievenblad: 66 wordt "0,066". */
export function toonTarief(tarief: TiendeCent): string {
  eisTiendeCent(tarief);
  const euros = Math.floor(tarief / 1_000);
  const rest = String(tarief % 1_000).padStart(3, '0');
  return `${euros},${rest}`;
}
